import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {Amplify} from 'aws-amplify'
import {associateWebAuthnCredential, deleteWebAuthnCredential, listWebAuthnCredentials} from 'aws-amplify/auth'
import {useEffect, useState} from 'react'
import {MdAdd, MdSecurity, MdCheckCircle, MdError, MdInfo} from 'react-icons/md'

import {createAwsConfig} from '../../aws/exports'
import PasskeyMetadataService, {PasskeyMetadata} from '../../services/passkey-metadata'
import {getDeviceInfo, suggestDeviceName} from '../../utils/deviceInfo'
import {PlainButton} from '../shared/Button'
import {ConfirmationModal} from '../shared/ConfirmationModal'
import {PasskeyCard} from './PasskeyCard'
import {PasskeyBackupRecommendation} from './PasskeyBackupRecommendation'

// Helper to get current RP ID
export const getRelyingPartyId = (): string => {
    const hostname = window.location.hostname
    if (hostname === 'localhost' || hostname === '127.0.0.1') return 'localhost'
    if (hostname.endsWith('lucenthealth.com')) return 'lucenthealth.com'
    if (hostname.endsWith('narushealth.com')) return 'narushealth.com'
    return hostname
}

interface WebAuthnCredential {
    credentialId: string
    friendlyName?: string
    createdAt: number
    relyingPartyId: string
}

interface EnrichedCredential extends WebAuthnCredential {
    metadata?: PasskeyMetadata
}

export function AuthorizationMethodSettings(): JSX.Element {
    const [isAdding, setIsAdding] = useState(false)
    const [deviceName, setDeviceName] = useState('')
    const [status, setStatus] = useState('')
    const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; credentialId: string; name: string }>({
        isOpen: false,
        credentialId: '',
        name: ''
    })
    const queryClient = useQueryClient()

    // Ensure Amplify is configured with correct RP ID on component mount
    useEffect(() => {
        const awsConfig = createAwsConfig()
        // Re-configure Amplify to ensure fresh RP ID
        Amplify.configure(awsConfig as any)
    }, [])

    // Fetch WebAuthn credentials and metadata
    const {data: enrichedCredentials, isLoading, error} = useQuery({
        queryKey: ['webauthn-credentials'],
        queryFn: async () => {
            // Fetch both Cognito credentials and our metadata
            const [cognitoResult, metadataList] = await Promise.all([
                listWebAuthnCredentials(),
                PasskeyMetadataService.getAll()
            ])

            // Map Cognito credentials with metadata
            const enriched = ((cognitoResult.credentials || []) as any[]).map(cred => {
                const metadata = metadataList.find(m => m.credential_id === cred.credentialId)
                return {
                    credentialId: cred.credentialId,
                    friendlyName: metadata?.device_name || cred.friendlyName,
                    createdAt: cred.createdAt,
                    relyingPartyId: cred.relyingPartyId,
                    metadata
                } as EnrichedCredential
            })

            // Clean up orphaned metadata
            const activeIds = enriched.map(c => c.credentialId)
            if (metadataList.length > 0) {
                PasskeyMetadataService.cleanupOrphaned(activeIds).catch(() => {
                })
            }

            return enriched
        }
    })

    const credentialList = enrichedCredentials ?? []

    // Register new credential mutation
    const registerMutation = useMutation({
        mutationFn: async (deviceName: string) => {
            setStatus('Preparing registration...')

            try {
                setStatus('Please use your device\'s built-in authentication (Touch ID, Face ID, or Windows Hello)...')

                // Use AWS Amplify's native WebAuthn registration
                let registeredCredentialId: string | undefined
                try {
                    const result = await associateWebAuthnCredential()
                    // Extract credential ID from the result
                    registeredCredentialId = (result as any)?.credentialId
                } catch (platformError: any) {
                    // Check if this is the RP mismatch error from Cognito
                    if (platformError.message?.includes('Relying party does not match')) {
                        setStatus('❌ WebAuthn registration failed: AWS Cognito configuration issue.')
                        throw platformError
                    }

                    // Provide user guidance
                    if (platformError.name === 'NotAllowedError') {
                        setStatus('❌ Registration cancelled or timed out.')
                        throw platformError
                    }

                    throw platformError
                }

                // Save metadata with enhanced device info
                if (registeredCredentialId || deviceName) {
                    try {
                        // Get comprehensive device information
                        const deviceInfo = getDeviceInfo('platform')
                        const finalDeviceName = deviceName || suggestDeviceName(deviceInfo)

                        // Get latest credentials to find the newly registered one
                        const latestResult = await listWebAuthnCredentials()
                        const newCredential = ((latestResult.credentials || []) as any[])
                            .sort((a, b) => b.createdAt - a.createdAt)[0]

                        if (newCredential?.credentialId) {
                            // Send all enhanced metadata
                            await PasskeyMetadataService.upsert({
                                credentialId: newCredential.credentialId,
                                deviceName: finalDeviceName,
                                browser: `${deviceInfo.browser} ${deviceInfo.browserVersion}`.trim(),
                                operating_system: `${deviceInfo.os} ${deviceInfo.osVersion}`.trim(),
                                device_type: deviceInfo.deviceType,
                                is_synced: deviceInfo.isSynced,
                                sync_provider: deviceInfo.syncProvider,
                                authenticator_attachment: deviceInfo.authenticatorAttachment,
                                platform: deviceInfo.platform,
                                screen_resolution: deviceInfo.screenResolution,
                                timezone: deviceInfo.timezone,
                                language: deviceInfo.language
                            })
                        }
                    } catch (metadataError) {
                        console.error('Failed to save device metadata:', metadataError)
                        // Don't fail the whole registration if metadata save fails
                    }
                }

                setStatus('✅ Security key registered successfully!')

                // Reset form
                setIsAdding(false)
                setDeviceName('')

                // Refresh credentials list
                await queryClient.invalidateQueries({queryKey: ['webauthn-credentials']})

            } catch (error: any) {
                if (error.name === 'NotAllowedError') {
                    setStatus('❌ Registration was cancelled or timed out')
                } else if (error.name === 'InvalidStateError') {
                    setStatus('❌ This security key may already be registered')
                } else if (error.message?.includes('Relying party does not match')) {
                    setStatus('❌ Domain not configured in AWS Cognito for WebAuthn')
                } else {
                    setStatus(`❌ Registration failed: ${error.message}`)
                }

                throw error
            }
        },
        onError: () => {
            setTimeout(() => setStatus(''), 5000)
        },
        onSuccess: () => {
            setTimeout(() => setStatus(''), 3000)
        }
    })

    // Delete credential mutation
    const deleteMutation = useMutation({
        mutationFn: async (credentialId: string) => {
            try {
                // Delete from Cognito first
                await deleteWebAuthnCredential({credentialId})

                // Then delete our metadata (don't fail if this errors)
                try {
                    await PasskeyMetadataService.delete(credentialId)
                } catch (metadataError) {
                    console.error('Failed to delete metadata:', metadataError)
                }
            } catch (error: any) {
                console.error('Delete error:', error)
                throw error
            }
        },
        onSuccess: () => {
            // Refresh the credentials list without page reload
            queryClient.invalidateQueries({queryKey: ['webauthn-credentials']})
            setStatus('✅ Security key removed successfully')
            setTimeout(() => setStatus(''), 3000)
        },
        onError: (error: any) => {
            console.error('Failed to remove security key:', error)
            setStatus('❌ Failed to remove security key. Please try again.')
            setTimeout(() => setStatus(''), 5000)
        }
    })

    // Update device name mutation with optimistic updates
    const updateNameMutation = useMutation({
        mutationFn: async ({credentialId, name}: { credentialId: string; name: string }) => {
            await PasskeyMetadataService.updateDeviceName(credentialId, name)
        },
        onMutate: async ({credentialId, name}) => {
            await queryClient.cancelQueries({queryKey: ['webauthn-credentials']})

            const previousData = queryClient.getQueryData<EnrichedCredential[] | undefined>(['webauthn-credentials'])

            queryClient.setQueryData(['webauthn-credentials'], (old: EnrichedCredential[] | undefined) => {
                if (!old) return old
                return old.map(cred =>
                    cred.credentialId === credentialId
                        ? {
                            ...cred,
                            friendlyName: name,
                            metadata: cred.metadata ? {...cred.metadata, device_name: name} : undefined
                        }
                        : cred
                )
            })

            return {previousData}
        },
        onError: (error, variables, context) => {
            if (context?.previousData) {
                queryClient.setQueryData(['webauthn-credentials'], context.previousData)
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({queryKey: ['webauthn-credentials']})
        }
    })

    const handleRegister = (deviceName: string) => {
        registerMutation.mutate(deviceName)
    }

    const confirmDelete = () => {
        if (deleteConfirm.credentialId) {
            deleteMutation.mutate(deleteConfirm.credentialId)
            setDeleteConfirm({isOpen: false, credentialId: '', name: ''})
        }
    }

    const handleUpdateName = async (credentialId: string, newName: string) => {
        if (newName.trim()) {
            // Return the mutation promise so PasskeyCard can handle errors
            return updateNameMutation.mutateAsync({credentialId, name: newName.trim()})
        }
    }

    // Check WebAuthn support
    const isWebAuthnSupported = !!(
        window.PublicKeyCredential &&
        navigator.credentials &&
        navigator.credentials.create
    )

    if (!isWebAuthnSupported) {
        return (
            <div className="security-keys-container p-4 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-yellow-800">
                    ⚠️ Your browser doesn't support security keys. Please use a modern browser like Chrome, Firefox,
                    Safari,
                    or Edge.
                </p>
            </div>
        )
    }

    return (
        <div>

            {/* Loading state */}
            {isLoading && (
                <div className="col-xs-12" style={{textAlign: 'center', padding: '20px 0'}}>
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                    <p style={{marginTop: '10px', color: '#666'}}>Loading security keys...</p>
                </div>
            )}

            {/* Error state */}
            {error && (
                <div className="col-xs-12" style={{marginBottom: '20px'}}>
                    <div style={{
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        color: '#b91c1c',
                        padding: '12px 16px',
                        borderRadius: '4px'
                    }}>
                        <p>Failed to load security keys. Please refresh the page.</p>
                    </div>
                </div>
            )}

            {/* Credentials list */}
            {!isLoading && !error && credentialList && (
                <div className="col-xs-12" style={{marginBottom: '20px'}}>
                    {credentialList.length === 0 && !isAdding ? (
                        <div style={{
                            backgroundColor: '#f9fafb',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            padding: '40px',
                            textAlign: 'center'
                        }}>
                            <MdSecurity style={{fontSize: '48px', color: '#9ca3af', margin: '0 auto 10px'}}/>
                            <p style={{color: '#4b5563', marginBottom: '4px'}}>No security keys registered yet</p>
                            <PlainButton
                                label="Add your first security key"
                                onClick={() => {
                                    setIsAdding(true)
                                    setDeviceName('')
                                }}
                                icon={<MdAdd/>}
                            />
                        </div>
                    ) : credentialList.length > 0 ? (
                        <div style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            backgroundColor: 'white'
                        }}>
                            {credentialList.map((credential, index) => (
                                <div key={credential.credentialId}
                                     style={{borderTop: index > 0 ? '1px solid #e5e7eb' : 'none'}}>
                                    <PasskeyCard
                                        credential={credential}
                                        onEdit={handleUpdateName}
                                        onDelete={(credentialId, name) => {
                                            setDeleteConfirm({
                                                isOpen: true,
                                                credentialId,
                                                name
                                            })
                                        }}
                                        isDeleting={deleteMutation.isPending}
                                    />
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
            )}

            {/* Backup recommendation - show when user has only 1 passkey and not adding */}
            {!isLoading && !error && credentialList.length === 1 && !isAdding && (
                <div className="col-xs-12">
                    <PasskeyBackupRecommendation
                        credentialCount={credentialList.length}
                        onAddPasskey={() => {
                            setIsAdding(true)
                            setDeviceName('')
                        }}
                    />
                </div>
            )}

            {/* Add new credential button - only show when there are 2+ credentials and not adding */}
            {!isAdding && credentialList.length > 1 && (
                <div className="col-xs-12" style={{marginBottom: '20px'}}>
                    <PlainButton
                        label="Add Security Key"
                        onClick={() => {
                            setIsAdding(true)
                            setDeviceName('')
                        }}
                        disabled={registerMutation.isPending}
                        icon={<MdAdd/>}
                    />
                </div>
            )}

            {/* Add new credential form - show when isAdding is true */}
            {isAdding && (
                <div className="col-xs-12 m-0 p-0">
                    <style>{`
                        .device-name-input::placeholder {
                            font-weight: 400;
                        }
                        .device-name-input:hover {
                            border-color: #7728ff;
                        }
                        .device-name-input:focus {
                            border-color: #7728ff;
                        }
                    `}</style>
                    <div style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '24px',
                        margin: '16px'
                    }}>
                        {/* Header with icon and title */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '16px'
                        }}>
                            <MdSecurity style={{fontSize: '24px', color: '#4b5563', marginRight: '8px'}}/>
                            <h4 style={{margin: 0, fontSize: '18px', fontWeight: '600', color: '#374151'}}>
                                Add Security Key
                            </h4>
                        </div>

                        {/* Note about password managers */}
                        <div style={{
                            padding: '12px',
                            backgroundColor: '#fefce8',
                            border: '1px solid #fef08a',
                            borderRadius: '4px',
                            marginBottom: '20px'
                        }}>
                            <p style={{fontSize: '13px', color: '#854d0e', marginBottom: '0', lineHeight: '1.5'}}>
                                <strong>Note:</strong> If your password manager pops up (like LastPass), you can save
                                the passkey — or cancel it to use your device. If it keeps showing up, try a private
                                window.
                            </p>
                        </div>

                        <div>
                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    color: '#111827',
                                    marginBottom: '8px',
                                    paddingLeft: '4px'
                                }}>
                                    Name your device (optional)
                                </label>
                                <input
                                    type="text"
                                    className="device-name-input"
                                    value={deviceName}
                                    onChange={(e) => setDeviceName(e.target.value)}
                                    placeholder="e.g., MacBook Pro, YubiKey, iPhone"
                                    style={{
                                        width: '100%',
                                        maxWidth: '500px',
                                        padding: '8px 12px',
                                        border: '2px solid #d1d5db',
                                        borderRadius: '6px',
                                        fontSize: '16px',
                                        fontWeight: '500',
                                        marginBottom: '8px',
                                        outline: 'none',
                                        transition: 'border-color 0.2s'
                                    }}
                                    onMouseEnter={(e) => (e.target as HTMLInputElement).style.borderColor = '#7728ff'}
                                    onMouseLeave={(e) => {
                                        if (document.activeElement !== e.target) {
                                            (e.target as HTMLInputElement).style.borderColor = '#d1d5db'
                                        }
                                    }}
                                    onFocus={(e) => (e.target as HTMLInputElement).style.borderColor = '#7728ff'}
                                    onBlur={(e) => (e.target as HTMLInputElement).style.borderColor = '#d1d5db'}
                                />
                                {/* Instructions under input */}
                                <div style={{
                                    fontSize: '13px',
                                    color: '#6b7280',
                                    marginBottom: '24px',
                                    lineHeight: '1.5'
                                }}>
                                    <p style={{margin: '0 0 4px 4px'}}>
                                        Supported: Touch ID, Face ID, Windows Hello, YubiKey, or your phone
                                    </p>
                                </div>

                                <div style={{
                                    display: 'flex',
                                    gap: '10px',
                                    marginBottom: '10px',
                                    justifyContent: 'flex-end',
                                    maxWidth: '528px'
                                }}>
                                    <PlainButton
                                        label="Cancel"
                                        onClick={() => {
                                            setIsAdding(false)
                                            setStatus('')
                                            setDeviceName('')
                                        }}
                                        disabled={registerMutation.isPending}
                                        btnType="plain"
                                    />
                                    <PlainButton
                                        label={registerMutation.isPending ? 'Registering...' : 'Register Security Key'}
                                        onClick={() => handleRegister(deviceName || 'Security Key')}
                                        disabled={registerMutation.isPending}
                                        color="primary"
                                        btnType="contained"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Status message */}
                        {status && (
                            <div style={{
                                marginTop: '20px',
                                padding: '14px 18px',
                                borderRadius: '6px',
                                fontSize: '14px',
                                maxWidth: '526px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                backgroundColor: status.includes('✅') ? '#f0fdf4' : status.includes('❌') ? '#fef2f2' : '#eff6ff',
                                color: status.includes('✅') ? '#15803d' : status.includes('❌') ? '#991b1b' : '#1e40af',
                                border: status.includes('✅') ? '1px solid #86efac' : status.includes('❌') ? '1px solid #fecaca' : '1px solid #bfdbfe',
                                lineHeight: '1.5'
                            }}>
                                {status.includes('✅') ? (
                                    <MdCheckCircle style={{fontSize: '22px', flexShrink: 0}}/>
                                ) : status.includes('❌') ? (
                                    <MdError style={{fontSize: '22px', flexShrink: 0}}/>
                                ) : (
                                    <MdInfo style={{fontSize: '22px', flexShrink: 0}}/>
                                )}
                                <span style={{flex: 1}}>
                                    {status.replace('✅', '').replace('❌', '').trim()}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={deleteConfirm.isOpen}
                title="Remove Security Key"
                message={`Are you sure you want to remove "${deleteConfirm.name}"? You won't be able to use this security key to sign in anymore.`}
                confirmText="Remove"
                cancelText="Cancel"
                variant="danger"
                onConfirm={confirmDelete}
                onCancel={() => setDeleteConfirm({isOpen: false, credentialId: '', name: ''})}
                isLoading={deleteMutation.isPending}
            />
        </div>
    )
}
